/* global BigInt */
/**
 * Mirrors Dart VM `dart:math` `Random(int seed)` — same stream as Flutter for
 * Hidden Words, Word Square, and other games that use `Random(seed)` on-device.
 *
 * Flutter uses the VM implementation (see `sdk/lib/_internal/vm/lib/math_patch.dart`).
 *
 * **`_Random._setupSeed` is implicitly 64-bit:** each mixing step wraps `mod 2^64`
 * (`BigInt.asUintN(64, …)`), matching the VM compiler’s fixed-width behavior.
 */

const POW2_32 = 0x100000000;
const A = 0xffffda61n;

/** @param {bigint} x */
function u64(x) {
  return BigInt.asUintN(64, x);
}

/**
 * VM `_Random._setupSeed` — must wrap each step at 64 bits.
 * @param {bigint} seed
 */
function setupVmSeed(seed) {
  let n = u64(seed);
  n = u64(~n + (n << 21n));
  n = u64(n ^ (n >> 24n));
  n = u64(n * 265n);
  n = u64(n ^ (n >> 14n));
  n = u64(n * 21n);
  n = u64(n ^ (n >> 28n));
  n = u64(n + (n << 31n));
  if (n === 0n) {
    return u64(0x5a17n);
  }
  return n;
}

export class DartRandom {
  /** @param {number} seed */
  constructor(seed) {
    /** @private @type {bigint} */
    this._state = setupVmSeed(BigInt(Math.trunc(seed)));
    this._nextState();
    this._nextState();
    this._nextState();
    this._nextState();
  }

  _nextState() {
    const lo = this._state & 0xffffffffn;
    const hi = this._state >> 32n;
    this._state = A * lo + hi;
  }

  /**
   * @param {number} max 0 < max ≤ 2^32 (VM rejects some cases above 2^30 per its range check)
   */
  nextInt(max) {
    const limit = 0x3fffffff;
    if (max <= 0 || (max > limit && max > POW2_32)) {
      throw RangeError(`max must be in range 1..2^32, was ${max}`);
    }
    if ((max & -max) === max) {
      this._nextState();
      return Number(this._state & 0xffffffffn) & (max - 1);
    }

    /** @type {number} */
    let rnd32;
    /** @type {number} */
    let result;
    do {
      this._nextState();
      rnd32 = Number(this._state & 0xffffffffn);
      result = rnd32 % max;
    } while (rnd32 - result + max > POW2_32);
    return result;
  }
}

/** @param {number} seed */
export function createDartRandom(seed) {
  return new DartRandom(seed);
}
